import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconX } from "@tabler/icons-react";

import { fetchAllRoles } from "@/api/roles-permissions/roles-permissions.queries";
import { Checkbox } from "@/components/ui/Checkbox/Checkbox";
import { Input } from "@/components/ui/Input/Input";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";

import styles from "./RolePicker.module.scss";

const ROLES_PAGE_SIZE = "50";

export interface RolePickerProps {
  selectedIds: string[];
  onChange: (roleIds: string[]) => void;
}

export function RolePicker({ selectedIds, onChange }: RolePickerProps) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const knownNames = useRef(new Map<string, string>());

  const rolesQuery = useQuery({
    queryKey: ["fileShareRoles", debouncedSearch],
    queryFn: ({ signal }) =>
      fetchAllRoles({ search: debouncedSearch, page: "1", pageSize: ROLES_PAGE_SIZE, signal }),
  });

  const roles = (rolesQuery.data?.data ?? []).filter(
    (role) => role.status !== "inactive" || selectedIds.includes(role.id),
  );
  roles.forEach((role) => knownNames.current.set(role.id, role.name));

  const toggle = (roleId: string, checked: boolean) =>
    onChange(checked ? [...selectedIds, roleId] : selectedIds.filter((id) => id !== roleId));

  return (
    <div className={styles.root}>
      {selectedIds.length > 0 && (
        <ul className={styles.chips} aria-label="Selected roles">
          {selectedIds.map((roleId) => {
            const roleName = knownNames.current.get(roleId) ?? "Role";
            return (
              <li key={roleId} className={styles.chip}>
                <span>{roleName}</span>
                <button
                  type="button"
                  className={styles.remove}
                  aria-label={`Remove ${roleName}`}
                  onClick={() => toggle(roleId, false)}
                >
                  <IconX size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <Input
        label="Roles"
        className={styles.search}
        placeholder="Search roles"
        value={search}
        onChange={setSearch}
        autoComplete="off"
      />
      <div className={styles.list} role="group" aria-label="Role list">
        {rolesQuery.isLoading ? (
          <span className={styles.empty}>Loading...</span>
        ) : roles.length === 0 ? (
          <span className={styles.empty}>No roles found</span>
        ) : (
          roles.map((role) => (
            <div key={role.id} className={styles.row}>
              <Checkbox
                label={role.name}
                state={selectedIds.includes(role.id) ? "selected" : "deselected"}
                onValueChange={(checked) => toggle(role.id, checked)}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
